// The uploader in its own browser tab.
//
// A gallery's Upload tab is a launcher: "Open uploader" opens the full-screen uploader (UploadApp) on the
// SAME host at /admin/upload, deep-linked to the gallery and collection. Same origin on purpose — the
// Cognito session lives in this origin's storage, so the uploader tab is already signed in, shares the
// "Sign in again" recovery with this tab, and keeps this browser's uploader settings and resume records.
// (/admin/upload rather than /upload: on a tenant host only /admin/* reaches this app.)
//
// One uploader tab: it's opened under a fixed window name, so a second click finds it and brings it to
// the front — pointing it at the new destination over a BroadcastChannel, which it follows with an
// in-app navigation (UploaderTabBridge) — instead of reloading it, which would cut off its uploads, or
// opening a duplicate that could upload the same folder twice.
//
// Every tab with uploads also reports them on the channel (the light job fields the status needs), so a
// gallery's launcher can show how uploads to that gallery are going in the other tab.
import type { TimelineJob } from "./uploadTimeline";

export const UPLOADER_WINDOW = "photohawk-uploader";
const CHANNEL = "photohawk-uploader";
/** The uploader's path on a tenant / custom-domain host (also served at /upload and upload.<domain>). */
export const UPLOADER_PATH = "/admin/upload";

/** Where the uploader should open: workspace (alias or custom domain), gallery (engine guid or slug),
 *  collection (guid). */
export interface UploaderTarget {
  workspace?: string;
  gallery?: string;
  collection?: string;
}

export type UploaderMessage =
  /** Launcher → every tab: report your uploads now. */
  | { type: "hello"; from: string }
  /** A tab with uploads → launchers: its uploads (light fields only), on change and every few seconds. */
  | { type: "status"; from: string; jobs: TimelineJob[]; uploader: boolean; at: number }
  /** Launcher → the uploader tab: show this destination ("Open uploader" when it's already open). */
  | { type: "goto"; from: string; target: UploaderTarget }
  /** A tab that reported uploads is closing. */
  | { type: "bye"; from: string };

/** This tab's id on the channel (so it can ignore its own messages). */
export const TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Is this tab the named uploader tab? */
export const isUploaderTab = (): boolean => {
  try {
    return typeof window !== "undefined" && window.name === UPLOADER_WINDOW;
  } catch {
    return false;
  }
};

/** A BroadcastChannel for uploader messages, or null where unsupported. */
export const openUploaderChannel = (): BroadcastChannel | null => {
  try {
    return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
};

export const post = (ch: BroadcastChannel | null, msg: UploaderMessage) => {
  try {
    ch?.postMessage(msg);
  } catch {
    /* channel closed — nothing to tell */
  }
};

/** /admin/upload?workspace=…&gallery=…&collection=… */
export const uploaderUrl = (t: UploaderTarget, path = UPLOADER_PATH): string => {
  const q = new URLSearchParams();
  if (t.workspace) q.set("workspace", t.workspace);
  if (t.gallery) q.set("gallery", t.gallery);
  if (t.collection) q.set("collection", t.collection);
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
};

/** The destination in an uploader URL's query (empty values dropped). */
export const targetFromSearch = (search: string | URLSearchParams): UploaderTarget | undefined => {
  const q = typeof search === "string" ? new URLSearchParams(search) : search;
  const t: UploaderTarget = {
    workspace: q.get("workspace")?.trim() || undefined,
    gallery: q.get("gallery")?.trim() || undefined,
    collection: q.get("collection")?.trim() || undefined,
  };
  return t.workspace || t.gallery ? t : undefined;
};

/** Is `w` a page of this app (same origin, past the blank page a fresh window.open starts on)? */
const isOurPage = (w: Window): boolean => {
  try {
    return w.location.href !== "about:blank" && w.location.origin === window.location.origin;
  } catch {
    return false; // another origin now — not ours to keep
  }
};

/**
 * Open the uploader at `target` in its own named tab, or bring the one that's already open to the front
 * and point it at `target` (no reload — its uploads keep going). Call from a click handler: browsers only
 * allow opening a tab in response to one. Returns what happened.
 */
export const openUploaderTab = (target: UploaderTarget, path = UPLOADER_PATH): "opened" | "focused" | "same-tab" => {
  const url = uploaderUrl(target, path);
  let w: Window | null = null;
  try {
    // "" finds the existing tab by name WITHOUT navigating it; otherwise opens a blank one we then point.
    w = window.open("", UPLOADER_WINDOW);
  } catch {
    w = null;
  }
  if (!w) {
    // Pop-ups blocked: open it here instead (the admin is a click away with Back).
    window.location.assign(url);
    return "same-tab";
  }
  if (isOurPage(w)) {
    // Already open (on the uploader or anywhere else in the app): it navigates in-app, so uploads
    // running in it carry on.
    const ch = openUploaderChannel();
    post(ch, { type: "goto", from: TAB_ID, target });
    ch?.close();
    try {
      w.focus();
    } catch {
      /* focus is best-effort */
    }
    return "focused";
  }
  w.location.href = url;
  try {
    w.focus();
  } catch {
    /* best-effort */
  }
  return "opened";
};

/** The light fields of a job the status needs (no per-file rows), for the channel. */
export const lightJob = (j: TimelineJob): TimelineJob => ({
  id: j.id,
  galleryName: j.galleryName,
  engineGuid: j.engineGuid,
  kind: j.kind,
  phase: j.phase,
  total: j.total,
  completed: j.completed,
  failed: j.failed,
  bytesTotal: j.bytesTotal,
  bytesSent: j.bytesSent,
  startedAt: j.startedAt,
  seq: j.seq,
  folder: j.folder,
  finishedAt: j.finishedAt,
  uploadingAt: j.uploadingAt,
  contentDoneAt: j.contentDoneAt,
  previewFirst: j.previewFirst,
  pairId: j.pairId,
  soldFirst: j.soldFirst,
  originalsBy: j.originalsBy,
  paused: j.paused,
  waitingForSignIn: j.waitingForSignIn,
  conc: j.conc,
  scan: j.scan,
  scanTotal: j.scanTotal,
  scanCompleted: j.scanCompleted,
});
